class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.requestSequence = 0;
    this.searchTerm = '';
    this.recentSearchesStorageKey = 'dearmora-recent-searches';
    this.recentSearchesLimit = 5;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onPredictiveSearchClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // Stop an in-flight request as soon as the field is cleared. Otherwise
      // its stale response can reopen/overwrite the dropdown after the next
      // search starts.
      this.abortController.abort();
      this.abortController = new AbortController();
      this.requestSequence += 1;
      this.showDefaultSuggestions();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    if (this.getQuery().length) this.saveRecentSearch(this.getQuery());
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.requestSequence += 1;
      requestAnimationFrame(() => this.showDefaultSuggestions());
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showDefaultSuggestions();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.showDefaultSuggestions();
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      const previousTermOccurrences = currentButtonText.split(previousTerm).length - 1;

      if (previousTermOccurrences > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const selectedOption = selectedElement?.matches('a, button')
      ? selectedElement
      : selectedElement?.querySelector('a, button');

    if (selectedOption) selectedOption.click();
  }

  onPredictiveSearchClick(event) {
    const searchSuggestion = event.target.closest('[data-search-suggestion]');

    if (searchSuggestion) {
      event.preventDefault();
      const searchTerm = searchSuggestion.dataset.searchSuggestion?.trim();

      if (!searchTerm) return;

      this.saveRecentSearch(searchTerm);
      this.input.value = searchTerm;
      this.input.form.requestSubmit();
      return;
    }

    if (event.target.closest('[data-clear-recent-searches]')) {
      event.preventDefault();
      this.clearRecentSearches();
      this.showDefaultSuggestions();
      return;
    }

    if (event.target.closest('a') && this.getQuery()) this.saveRecentSearch(this.getQuery());
  }

  getRecentSearches() {
    try {
      const searches = JSON.parse(localStorage.getItem(this.recentSearchesStorageKey) || '[]');
      return Array.isArray(searches) ? searches.filter((term) => typeof term === 'string' && term.trim()) : [];
    } catch (error) {
      return [];
    }
  }

  saveRecentSearch(searchTerm) {
    const normalizedTerm = searchTerm.trim();
    if (!normalizedTerm) return;

    const recentSearches = this.getRecentSearches().filter(
      (term) => term.toLocaleLowerCase() !== normalizedTerm.toLocaleLowerCase()
    );

    recentSearches.unshift(normalizedTerm);

    try {
      localStorage.setItem(
        this.recentSearchesStorageKey,
        JSON.stringify(recentSearches.slice(0, this.recentSearchesLimit))
      );
    } catch (error) {
      // Search must continue to work when local storage is unavailable.
    }
  }

  clearRecentSearches() {
    try {
      localStorage.removeItem(this.recentSearchesStorageKey);
    } catch (error) {
      // Ignore browsers that block local storage.
    }
  }

  getTopSearches() {
    const configuredSearches = this.dataset.topSearches ||
      'Christmas ornaments|Personalized gifts|Gifts for mom|Gifts for dad|Best sellers';

    return configuredSearches
      .split('|')
      .map((term) => term.trim())
      .filter(Boolean)
      .slice(0, 6);
  }

  escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = value;
    return element.innerHTML;
  }

  renderSuggestionItems(searches, prefix) {
    return searches
      .map((term, index) => {
        const escapedTerm = this.escapeHtml(term);

        return `
          <li
            id="predictive-search-option-${prefix}-${index + 1}"
            class="predictive-search__list-item"
            role="option"
            aria-selected="false"
          >
            <button
              class="predictive-search__item predictive-search__suggestion-link predictive-search__default-suggestion link link--text"
              type="button"
              data-search-suggestion="${escapedTerm}"
              tabindex="-1"
            >
              <span class="predictive-search__default-suggestion-icon" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none"><circle cx="9" cy="9" r="5.75" stroke="currentColor" stroke-width="1.5"/><path d="m13.25 13.25 3.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              </span>
              <span class="predictive-search__item-heading h5">${escapedTerm}</span>
              <span class="predictive-search__default-arrow" aria-hidden="true">&#8250;</span>
            </button>
          </li>`;
      })
      .join('');
  }

  showDefaultSuggestions() {
    if (this.getQuery().length) return;

    const recentSearches = this.getRecentSearches();
    const topSearches = this.getTopSearches();

    if (!recentSearches.length && !topSearches.length) {
      this.close();
      return;
    }

    const recentMarkup = recentSearches.length
      ? `
        <section class="predictive-search__default-section">
          <div class="predictive-search__default-heading-row">
            <h2 class="predictive-search__heading">Recent searches</h2>
            <button type="button" class="predictive-search__clear-recent" data-clear-recent-searches>Clear</button>
          </div>
          <ul class="predictive-search__results-list list-unstyled" role="group">
            ${this.renderSuggestionItems(recentSearches, 'recent')}
          </ul>
        </section>`
      : '';

    const topMarkup = topSearches.length
      ? `
        <section class="predictive-search__default-section">
          <h2 class="predictive-search__heading">Top searches</h2>
          <ul class="predictive-search__results-list list-unstyled" role="group">
            ${this.renderSuggestionItems(topSearches, 'top')}
          </ul>
        </section>`
      : '';

    this.predictiveSearchResults.innerHTML = `
      <div id="predictive-search-results" class="predictive-search__default-results" role="listbox">
        ${recentMarkup}${topMarkup}
      </div>`;
    this.setAttribute('results', true);
    this.open();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    // Cancel any older lookup even when this term can be served from cache.
    // This prevents an older network response from replacing cached results.
    this.abortController.abort();
    this.abortController = new AbortController();
    const requestSequence = ++this.requestSequence;

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchableProductFields = [
      'title',
      'product_type',
      'variants.title',
      'vendor',
      'tag',
      'variants.sku',
      'variants.barcode',
    ].join(',');
    const predictiveSearchUrl = new URL(routes.predictive_search_url, window.location.origin);

    predictiveSearchUrl.searchParams.set('q', searchTerm);
    predictiveSearchUrl.searchParams.set('section_id', 'predictive-search');
    predictiveSearchUrl.searchParams.set('resources[type]', 'query,product,collection,page,article');
    predictiveSearchUrl.searchParams.set('resources[limit]', '3');
    predictiveSearchUrl.searchParams.set('resources[limit_scope]', 'each');
    predictiveSearchUrl.searchParams.set('resources[options][fields]', searchableProductFields);
    predictiveSearchUrl.searchParams.set('resources[options][unavailable_products]', 'last');

    fetch(predictiveSearchUrl.toString(), {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (requestSequence !== this.requestSequence || this.getQuery() !== searchTerm) return;

        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          // The user typed/cleared another term before this request finished.
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
